import { useState, useEffect, useCallback } from 'react';
import * as pipelineApi from '../../api/pipelineApi';
import StageCard from '../../components/stages/StageCard/StageCard';
import ChecklistItemRow from '../../components/stages/ChecklistItemRow/ChecklistItemRow';
import StageFormModal from '../../components/stages/StageFormModal/StageFormModal';
import VersionCard from '../../components/stages/VersionCard/VersionCard';
import PublishModal from '../../components/stages/PublishModal/PublishModal';
import './StageTemplateManager.css';

export default function StageTemplateManager() {
  const [activeTab, setActiveTab] = useState('builder');
  const [draft, setDraft] = useState({ stages: [] });
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [showStageForm, setShowStageForm] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [dragState, setDragState] = useState({ draggingUid: null, overUid: null });
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [selectedStageUidForChecklist, setSelectedStageUidForChecklist] = useState(null);
  const [showAchievementBanner, setShowAchievementBanner] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const draftData = await pipelineApi.getDraft();
      const versionsData = await pipelineApi.getVersions();
      setDraft(draftData);
      setVersions(versionsData);
      if (draftData.stages?.length > 0 && !selectedStageUidForChecklist) {
        setSelectedStageUidForChecklist(draftData.stages[0].uid);
      }
    } catch (err) {
      showToast('Failed to load pipeline: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const calcHealthScore = () => {
    if (!draft.stages?.length) return 0;
    const healthy = draft.stages.filter(s => s.checklist?.length >= 3).length;
    return Math.round((healthy / draft.stages.length) * 100);
  };

  const healthScore = calcHealthScore();
  const healthColor = healthScore >= 75 ? 'var(--color-accent-green)' : healthScore >= 40 ? 'var(--color-accent-gold)' : 'var(--color-accent-red)';

  const handleSaveStage = async (stageData) => {
    try {
      setSaving(true);
      if (editingStage) {
        await pipelineApi.updateStage(editingStage.uid, stageData);
      } else {
        await pipelineApi.createStage(stageData);
      }
      const updatedDraft = await pipelineApi.getDraft();
      setDraft(updatedDraft);
      setShowStageForm(false);
      setEditingStage(null);
      showToast(editingStage ? 'Stage updated' : 'Stage created');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStage = async (uid) => {
    if (!window.confirm('Delete this stage and all its checklist items?')) return;
    try {
      setSaving(true);
      await pipelineApi.deleteStage(uid);
      const updatedDraft = await pipelineApi.getDraft();
      setDraft(updatedDraft);
      showToast('Stage deleted');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReorderStages = async (fromUid, toUid) => {
    if (fromUid === toUid) return;
    const fromIdx = draft.stages.findIndex(s => s.uid === fromUid);
    const toIdx = draft.stages.findIndex(s => s.uid === toUid);
    if (fromIdx === -1 || toIdx === -1) return;

    const newStages = [...draft.stages];
    const [movedStage] = newStages.splice(fromIdx, 1);
    newStages.splice(toIdx, 0, movedStage);
    newStages.forEach((s, i) => { s.order = i + 1; });
    setDraft({ ...draft, stages: newStages });

    try {
      const orderList = newStages.map((s, i) => ({ uid: s.uid, order: i + 1 }));
      await pipelineApi.reorderStages(orderList);
    } catch (err) {
      const revertedDraft = await pipelineApi.getDraft();
      setDraft(revertedDraft);
      showToast('Failed to reorder: ' + err.message, 'error');
    }
  };

  const selectedStage = draft.stages?.find(s => s.uid === selectedStageUidForChecklist);

  const handleUpdateChecklistItem = async (item) => {
    if (!selectedStage) return;
    const updatedChecklist = selectedStage.checklist.map(i => i.uid === item.uid ? item : i);
    updatedChecklist.sort((a, b) => a.order - b.order);
    try {
      await pipelineApi.updateChecklist(selectedStage.uid, updatedChecklist);
      const updatedDraft = await pipelineApi.getDraft();
      setDraft(updatedDraft);
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleDeleteChecklistItem = async (itemUid) => {
    if (!selectedStage) return;
    const updatedChecklist = selectedStage.checklist.filter(i => i.uid !== itemUid);
    try {
      await pipelineApi.updateChecklist(selectedStage.uid, updatedChecklist);
      const updatedDraft = await pipelineApi.getDraft();
      setDraft(updatedDraft);
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleAddChecklistItem = async () => {
    if (!selectedStage) return;
    const newItem = {
      text: 'New checklist item',
      required: true,
      hint: '',
      order: (selectedStage.checklist?.length || 0) + 1
    };
    const updatedChecklist = [...(selectedStage.checklist || []), newItem];
    try {
      await pipelineApi.updateChecklist(selectedStage.uid, updatedChecklist);
      const updatedDraft = await pipelineApi.getDraft();
      setDraft(updatedDraft);
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleReorderChecklistItems = async (fromIdx, toIdx) => {
    if (fromIdx === toIdx || !selectedStage) return;
    const newChecklist = [...selectedStage.checklist];
    const [movedItem] = newChecklist.splice(fromIdx, 1);
    newChecklist.splice(toIdx, 0, movedItem);
    newChecklist.forEach((item, i) => { item.order = i + 1; });
    try {
      await pipelineApi.updateChecklist(selectedStage.uid, newChecklist);
      const updatedDraft = await pipelineApi.getDraft();
      setDraft(updatedDraft);
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handlePublish = async (publishData) => {
    try {
      setSaving(true);
      const newVersion = await pipelineApi.publishDraft(publishData);
      const versionsData = await pipelineApi.getVersions();
      setVersions(versionsData);
      setShowPublishModal(false);
      setShowAchievementBanner(true);
      setTimeout(() => setShowAchievementBanner(false), 3000);
      showToast(`Version v${newVersion.version_number} Published!`);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleActivateVersion = async (versionId) => {
    if (!window.confirm('Restore this version? The draft will be replaced.')) return;
    try {
      setSaving(true);
      await pipelineApi.activateVersion(versionId);
      const draftData = await pipelineApi.getDraft();
      const versionsData = await pipelineApi.getVersions();
      setDraft(draftData);
      setVersions(versionsData);
      showToast('Version activated');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="stm"><div className="loading-state">Loading pipeline...</div></div>;

  const sortedStages = [...(draft.stages || [])].sort((a, b) => a.order - b.order);

  return (
    <div className="stm">
      {showAchievementBanner && (
        <div className="stm__achievement-banner">
          &#127881; Version Published! Configuration is now locked in the active version.
        </div>
      )}

      <div className="stm__header">
        <h1 className="stm__title">Stage & Template Builder</h1>
        <p className="stm__subtitle">Configure your client journey pipeline</p>
      </div>

      <div className="stm__tabs">
        {['builder', 'checklist', 'history'].map(tab => (
          <button
            key={tab}
            className={`stm__tab ${activeTab === tab ? 'stm__tab--active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'builder' ? 'Stage Builder' : tab === 'checklist' ? 'Checklist Editor' : 'Version History'}
          </button>
        ))}
      </div>

      {activeTab === 'builder' && (
        <>
          <div className="stm__health-bar">
            <span className="stm__health-label">Pipeline Health</span>
            <div className="stm__health-track">
              <div className="stm__health-fill" style={{ width: `${healthScore}%`, background: healthColor }}></div>
            </div>
            <span className="stm__health-score" style={{ color: healthColor }}>{healthScore}%</span>
          </div>

          <button className="btn btn--primary stm__add-btn" onClick={() => { setEditingStage(null); setShowStageForm(true); }}>
            + Create New Stage
          </button>

          <div className="stm__stage-grid">
            {sortedStages.map(stage => (
              <StageCard
                key={stage.uid}
                stage={stage}
                onEdit={s => { setEditingStage(s); setShowStageForm(true); }}
                onDelete={handleDeleteStage}
                isDragging={dragState.draggingUid === stage.uid}
                dragOver={dragState.overUid === stage.uid}
                onDragStart={() => setDragState({ draggingUid: stage.uid, overUid: null })}
                onDragOver={() => setDragState(d => ({ ...d, overUid: stage.uid }))}
                onDrop={() => { if (dragState.draggingUid) handleReorderStages(dragState.draggingUid, stage.uid); }}
                onDragEnd={() => setDragState({ draggingUid: null, overUid: null })}
              />
            ))}
          </div>
        </>
      )}

      {activeTab === 'checklist' && (
        <div className="stm__checklist-layout">
          <div className="stm__stage-list">
            {sortedStages.map(stage => (
              <button
                key={stage.uid}
                className={`stm__stage-list-item ${selectedStageUidForChecklist === stage.uid ? 'stm__stage-list-item--active' : ''}`}
                onClick={() => setSelectedStageUidForChecklist(stage.uid)}
              >
                {stage.icon} {stage.name}
              </button>
            ))}
          </div>

          {selectedStage ? (
            <div className="stm__checklist-editor">
              <div className="stm__editor-title">{selectedStage.name} Checklist</div>
              <div className="stm__stage-grid">
                {[...(selectedStage.checklist || [])].sort((a, b) => a.order - b.order).map((item, idx) => (
                  <ChecklistItemRow
                    key={item.uid}
                    item={item}
                    index={idx}
                    onUpdate={handleUpdateChecklistItem}
                    onDelete={handleDeleteChecklistItem}
                    isDragging={dragState.draggingUid === item.uid}
                    dragOver={dragState.overUid === item.uid}
                    onDragStart={() => setDragState({ draggingUid: item.uid, overUid: null })}
                    onDragOver={() => setDragState(d => ({ ...d, overUid: item.uid }))}
                    onDrop={() => {
                      const fromIdx = selectedStage.checklist.findIndex(i => i.uid === dragState.draggingUid);
                      const toIdx = selectedStage.checklist.findIndex(i => i.uid === item.uid);
                      if (fromIdx !== -1 && toIdx !== -1) handleReorderChecklistItems(fromIdx, toIdx);
                    }}
                    onDragEnd={() => setDragState({ draggingUid: null, overUid: null })}
                  />
                ))}
              </div>
              <button className="btn btn--primary stm__add-btn" onClick={handleAddChecklistItem}>
                + Add Checklist Item
              </button>
            </div>
          ) : (
            <div className="stm__checklist-editor stm__checklist-editor--empty">
              Select a stage to edit its checklist
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <>
          <button className="btn btn--primary stm__add-btn" onClick={() => setShowPublishModal(true)}>
            + Publish New Version
          </button>

          <div className="stm__stage-grid">
            {versions.map((version, idx) => (
              <VersionCard
                key={version._id}
                version={version}
                nextVersion={versions[idx + 1]}
                onActivate={handleActivateVersion}
                isActive={version.status === 'active'}
              />
            ))}
          </div>

          {versions.length === 0 && (
            <div className="stm__empty">No versions published yet. Create your first version to get started!</div>
          )}
        </>
      )}

      <StageFormModal
        isOpen={showStageForm}
        stage={editingStage}
        onSave={handleSaveStage}
        onClose={() => { setShowStageForm(false); setEditingStage(null); }}
      />

      <PublishModal
        isOpen={showPublishModal}
        healthScore={healthScore}
        nextVersionNumber={(versions?.[0]?.version_number || 0) + 1}
        onPublish={handlePublish}
        onClose={() => setShowPublishModal(false)}
        isPublishing={saving}
      />

      {toast && (
        <div className={`stm__toast ${toast.type === 'error' ? 'stm__toast--error' : 'stm__toast--success'}`}>
          {toast.type === 'error' ? '\u26A0' : '\u2713'} {toast.msg}
        </div>
      )}
    </div>
  );
}
